import { type FormEvent, useState } from "react";
import { useAuthenticatedUser } from "@/auth/auth-context";
import { getUserFacingErrorKey } from "@/lib/api/errors";
import { api } from "@/lib/api/services";
import { getFormString } from "@/lib/form-data";
import { type TranslationKey, useT } from "@/lib/i18n";
import { Modal } from "./modal";
import { Button } from "./ui/button";
import { TextField } from "./ui/text-field";

const SKIP_KEY = "onboarding_skipped";

function readSkipped(userId: string) {
  try { return window.localStorage.getItem(`${SKIP_KEY}:${userId}`) === "1"; } catch { return false; }
}

/** First-run prompt to pick a username; shown once for active accounts that have none, and can be skipped. */
export function OnboardingGate() {
  const { user, refreshUser } = useAuthenticatedUser();
  const t = useT();
  const userId = String(user.id);
  const [skipped, setSkipped] = useState(() => readSkipped(userId));
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<TranslationKey | "">("");

  if (user.username || skipped) return null;

  function skip() {
    try { window.localStorage.setItem(`${SKIP_KEY}:${userId}`, "1"); } catch { /* prompt may show again next session */ }
    setSkipped(true);
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const username = getFormString(new FormData(event.currentTarget), "username").trim();
    if (username.length < 3 || username.length > 20) { setError("auth.username_length"); return; }
    setPending(true); setError("");
    try {
      await api.setUsername(username);
      await refreshUser();
    } catch (requestError) {
      setError(getUserFacingErrorKey(requestError));
    } finally {
      setPending(false);
    }
  }

  return (
    <Modal title={t("auth.welcome")} onClose={skip} dismissible={!pending} closeDisabled={pending}>
      <form className="form-stack" onSubmit={(event) => void submit(event)}>
        <h3 className="onboarding-prompt">{t("auth.choose_username")}</h3>
        <p className="profile-muted">{t("auth.username_hint")}</p>
        <TextField label={t("auth.username_label")} name="username" minLength={3} maxLength={20} autoComplete="username" error={error ? t(error) : undefined} disabled={pending} required />
        <Button type="submit" disabled={pending}>{pending ? t("auth.saving") : t("auth.continue")}</Button>
        <Button type="button" variant="ghost" disabled={pending} onClick={skip}>{t("auth.skip")}</Button>
      </form>
    </Modal>
  );
}
